import React, { useEffect, useState, useCallback } from 'react';
import { api, apiUpload, downloadUrl, fmtDay, fmtWhen, KIND_WORDS, KIND_HINT, CLOSED, cmsSection, cmsSubsection } from './api.js';
import { Card, Button, ItemBadge, StatusPill, KindPill, Field, inputCls, Pill, Empty } from './ui.jsx';
import Discussion from './Discussion.jsx';
import Assistant from './Assistant.jsx';
import { AskComplianceButton } from './AskCompliance.jsx';

const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.eml,.msg';

const draftOf = (it) => ({
  accountable_user_ids: Array.isArray(it.accountable_user_ids) && it.accountable_user_ids.length ? it.accountable_user_ids : (it.owner_user_id ? [it.owner_user_id] : []),
  department_ids: Array.isArray(it.department_ids) && it.department_ids.length ? it.department_ids : (it.department_id ? [it.department_id] : []),
  due_date: it.due_date ? String(it.due_date).slice(0, 10) : '', kind: it.kind,
});

export default function ItemPage({ id, boot, people, onChanged, onClose, initialItem = null }) {
  const [item, setItem] = useState(initialItem); // initialItem: the render harness only
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null); // plain wording draft
  const [assign, setAssign] = useState(() => (initialItem ? draftOf(initialItem) : null)); // owner panel draft
  const [reason, setReason] = useState('');
  const [files, setFiles] = useState([]);
  const [fileNote, setFileNote] = useState('');
  const [how, setHow] = useState('');
  const [checking, setChecking] = useState(false);
  const [checkNote, setCheckNote] = useState(null);
  const [confirmReady, setConfirmReady] = useState(false); // nothing attached, no policy accepted: say so first
  const compliance = boot.is_compliance;

  const load = useCallback(async () => {
    const d = await api(`/items/${id}`);
    if (!d?.success) { setErr(d?.message || 'Could not load the item'); return; }
    setItem(d.item);
    setAssign(draftOf(d.item));
  }, [id]);
  useEffect(() => { if (!initialItem) load(); }, [load, initialItem]);

  const patch = async (body) => {
    setBusy(true); setErr(null);
    const d = await api(`/items/${id}`, { method: 'PATCH', body });
    setBusy(false);
    if (!d?.success) { setErr(d?.message || 'Could not save'); return false; }
    setItem(d.item); onChanged?.(); return true;
  };

  const post = async (path, body) => {
    setBusy(true); setErr(null);
    const d = await api(path, { method: 'POST', body });
    setBusy(false);
    if (!d?.success) { setErr(d?.message || 'Could not save'); return null; }
    if (d.item) setItem(d.item); onChanged?.(); return d;
  };
  const uploadFiles = async () => {
    if (!files.length) return;
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    if (fileNote.trim()) fd.append('note', fileNote.trim());
    setBusy(true); setErr(null);
    const d = await apiUpload(`/items/${id}/evidence`, fd);
    setBusy(false);
    if (!d?.success) { setErr(d?.message || 'Could not upload'); return; }
    setItem(d.item); setFiles([]); setFileNote(''); onChanged?.();
  };
  const runCheck = async () => {
    setChecking(true); setCheckNote(null); setErr(null);
    const d = await api(`/items/${id}/policy-check`, { method: 'POST', body: {} });
    setChecking(false);
    if (!d?.success) { setErr(d?.message || 'The policy check did not run'); return; }
    setItem(d.item); onChanged?.();
    setCheckNote(d.candidates === 0 ? 'No policy in the library speaks to this item.' : d.found.length === 0 ? `Read ${d.candidates} candidate polic${d.candidates === 1 ? 'y' : 'ies'}; none establishes this process.` : `Found ${d.found.length} polic${d.found.length === 1 ? 'y' : 'ies'} that may cover this. Decide below.`);
  };

  if (err && !item) return <Card><div className="text-sm text-rose-600">{err}</div></Card>;
  if (!item) return <Card><Empty>Loading…</Empty></Card>;
  const closed = CLOSED.has(item.status);
  const policyLines = (item.lines || []).filter((l) => l.kind === 'policy');
  const ownerLines = (item.lines || []).filter((l) => l.kind !== 'policy');
  const proposed = policyLines.filter((l) => l.status === 'proposed');
  const acceptedPolicy = policyLines.some((l) => l.status === 'accepted');
  const hasEvidence = (item.evidence || []).length > 0;
  const nothingBehindIt = !hasEvidence && !acceptedPolicy;
  const markReady = (acknowledged = false) => {
    if (nothingBehindIt && !acknowledged) { setConfirmReady(true); return; }
    setConfirmReady(false);
    return patch({ status: 'ready', reason, closed_how: 'confirmation', ...(nothingBehindIt ? { nothing_attached_acknowledged: true } : {}) });
  };
  const contacts = Array.isArray(item.contacts) ? item.contacts : [];
  const details = Array.isArray(item.cms_details) ? item.cms_details : [];
  const citations = Array.isArray(item.citations) ? item.citations : [];

  return (
    <div className="space-y-5">
      {/* Header */}
      <Card>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <ItemBadge cmsRef={item.cms_ref} seq={item.seq} />
              <span className="text-sm text-slate-600">{cmsSection(item)} · {cmsSubsection(item)}</span>
              <KindPill kind={item.kind} />
              <StatusPill status={item.status} />
              {(item.delegated_entity_names || []).length > 0 && <Pill tone="bg-violet-50 text-violet-800 ring-violet-200">Answered by {item.delegated_entity_names.join(', ')} · internal oversight only</Pill>}
            </div>
            <h2 className="text-lg font-bold text-slate-900 mt-2 leading-snug">{item.plain_requirement || item.cms_text}</h2>
            <div className="text-xs text-slate-500 mt-1">Applies to: {item.applies_to_text || 'all organizations'} · {KIND_WORDS[item.kind]}: {KIND_HINT[item.kind]} · Due {fmtDay(item.due_date)}</div>
          </div>
          <div className="text-right text-sm max-w-sm">
            {(() => {
              const depts = Array.isArray(item.departments) && item.departments.length ? item.departments : (item.department_id ? [{ id: item.department_id, name: item.department_name }] : []);
              const acc = Array.isArray(item.accountable_people) ? item.accountable_people : [];
              if (!depts.length) return <div className="font-semibold text-rose-700">No department</div>;
              return depts.map((d) => {
                const names = acc.filter((p) => p.department_id === d.id).map((p) => p.name);
                return (
                  <div key={d.id} className="mb-1">
                    <span className="font-semibold text-slate-800">{d.name}</span>
                    <span className="text-xs text-slate-500"> · {names.length ? `${names.join(', ')} accountable` : 'no one accountable'}</span>
                    {compliance && <a className="ml-2 text-[11px] font-semibold text-orange-800 underline" href={downloadUrl(`/cycles/${item.cycle_id}/export.xlsx?department=${encodeURIComponent(d.id)}`)} title={`Every ${d.name} item in Excel, in the plan's format with the logo.`}>Excel</a>}
                  </div>
                );
              });
            })()}
            {(item.assignee_names || []).length > 0 && <div className="text-xs text-slate-500">On it: {item.assignee_names.join(', ')}</div>}
            {!compliance && <div className="mt-2"><AskComplianceButton topicKey={item.topic_key} itemId={item.id} label={`${cmsSection(item)} · ${cmsSubsection(item)}`} /></div>}
          </div>
        </div>
        {!item.applicable && (
          <div className="mt-3 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">Does not apply to this plan: {item.na_reason || item.applicability_reason}.</div>
        )}
      </Card>

      {/* CMS, verbatim */}
      <Card title="What CMS says, in CMS's words" tone="border-orange-200" right={<span className="text-xs text-slate-500">verbatim from the CY {boot.edition.contract_year} checklist</span>}>
        <p className="text-sm text-slate-800 leading-relaxed">{item.cms_text}</p>
        {details.length > 0 && (
          <ul className="mt-2 space-y-1 pl-5 list-disc text-sm text-slate-700 leading-relaxed">{details.map((d, i) => <li key={i}>{d}</li>)}</ul>
        )}
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600">
          {citations.length > 0 && <span><b>Citation</b> {citations.join('; ')}</span>}
          {contacts.length > 0 && <span><b>CMS contact</b> {contacts.join(' · ')}</span>}
          <span><b>Topic</b> {item.topic_cms_title}</span>
        </div>
      </Card>

      {/* Plain words */}
      <Card title="In plain words" right={compliance && !edit ? <Button kind="secondary" onClick={() => setEdit({ plain_requirement: item.plain_requirement || '', plain_meaning: item.plain_meaning || '', plain_evidence: item.plain_evidence || '' })}>{item.wording_approved_at ? 'Edit wording' : 'Write the wording'}</Button> : null}>
        {edit ? (
          <div className="space-y-3">
            <Field label="What CMS requires, in one sentence"><textarea rows={2} className={inputCls} value={edit.plain_requirement} onChange={(e) => setEdit({ ...edit, plain_requirement: e.target.value })} /></Field>
            <Field label="What this means for us"><textarea rows={3} className={inputCls} value={edit.plain_meaning} onChange={(e) => setEdit({ ...edit, plain_meaning: e.target.value })} /></Field>
            <Field label="What to send"><textarea rows={2} className={inputCls} value={edit.plain_evidence} onChange={(e) => setEdit({ ...edit, plain_evidence: e.target.value })} /></Field>
            <div className="flex gap-2"><Button onClick={async () => { if (await patch(edit)) setEdit(null); }} disabled={busy}>Approve this wording</Button><Button kind="secondary" onClick={() => setEdit(null)}>Cancel</Button></div>
            <div className="text-xs text-slate-500">Approved wording is what every owner sees, this year and next. The CMS text above never changes.</div>
          </div>
        ) : item.wording_approved_at ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <Box label="What CMS requires">{item.plain_requirement}</Box>
            <Box label="What this means for us">{item.plain_meaning}</Box>
            <Box label="What to send">{item.plain_evidence}</Box>
          </div>
        ) : (
          <Empty>No plain-words version yet. Owners see the CMS text until compliance approves a plain version.</Empty>
        )}
      </Card>

      {/* Talk about it, ask about it */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-5 items-start">
        <div className="xl:col-span-3"><Discussion item={item} boot={boot} initialComments={initialItem ? initialItem.initial_comments || null : null} /></div>
        <div className="xl:col-span-2"><Assistant boot={boot} item={item} initialMessages={initialItem ? initialItem.initial_messages || null : null} /></div>
      </div>

      {/* Do we already have this? Policies that may cover the item. */}
      <Card title="Do we already have this? Policies that may cover it" tone={proposed.length ? 'border-blue-200' : ''}
        right={compliance ? <Button kind="secondary" disabled={checking || busy} onClick={runCheck}>{checking ? 'Reading the policy library…' : policyLines.length ? 'Check again' : 'Check our policies'}</Button> : <span className="text-xs text-slate-500">compliance checks the policy library against this item</span>}>
        {checkNote && <div className="text-sm text-slate-700 mb-3">{checkNote}</div>}
        {policyLines.length === 0 ? (
          <Empty>{compliance ? 'Not checked yet. The check reads every current policy, finds the ones that speak to this item, and quotes the passage that establishes the process.' : 'No policy has been matched to this item yet.'}</Empty>
        ) : (
          <div className="space-y-2">
            {policyLines.map((l) => (
              <div key={l.id} className={`border rounded-xl px-3 py-2.5 text-sm ${l.status === 'accepted' ? 'border-blue-200 bg-blue-50/40' : l.status === 'rejected' ? 'border-slate-200 opacity-60' : 'border-slate-200'}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Pill tone={l.verdict === 'covered' ? 'bg-blue-50 text-blue-800 ring-blue-200' : 'bg-amber-50 text-amber-800 ring-amber-200'}>{l.verdict === 'covered' ? 'Covers it' : 'Partly covers it'}</Pill>
                  <b className="text-slate-900">{l.policy_title}</b>
                  {l.policy_section && <span className="text-slate-600">· {l.policy_section}</span>}
                  {l.policy_version && <span className="text-xs text-slate-400">v{l.policy_version}</span>}
                  <span className="ml-auto text-xs text-slate-500">{l.status === 'accepted' ? `Accepted${l.decision_note ? `: ${l.decision_note}` : ''}` : l.status === 'rejected' ? `Set aside${l.decision_note ? `: ${l.decision_note}` : ''}` : 'Proposed by the agent'}</span>
                </div>
                {l.quote && <blockquote className="mt-2 border-l-2 border-blue-200 pl-3 text-slate-700 italic leading-relaxed">“{l.quote}”</blockquote>}
                {l.gap && <div className="mt-1.5 text-amber-800"><b>What it does not say:</b> {l.gap}</div>}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {l.policy_id && <a className="text-xs font-semibold text-orange-800 underline" href={`/app?app=ppm&policy=${encodeURIComponent(l.policy_id)}`} target="_blank" rel="noreferrer">Open the policy</a>}
                  {compliance && l.status === 'proposed' && !closed && (
                    <>
                      <Button kind="good" disabled={busy} onClick={() => post(`/lines/${l.id}/decide`, { decision: 'accept', note: reason || null })}>Accept and close the item</Button>
                      <Button kind="secondary" disabled={busy} onClick={() => post(`/lines/${l.id}/decide`, { decision: 'reject', note: reason || null })}>Not enough</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="text-xs text-slate-500 mt-3">A quote is shown only when it was found word for word in the policy. Accepting closes the item "by policy" and records your name; the policy itself is not changed.</div>
      </Card>

      {/* Evidence and the owner's answer */}
      <Card title={item.kind === 'confirm' ? 'Confirm it, or attach what shows it' : item.kind === 'read' ? 'Acknowledge it, or attach what shows it' : 'Attach what shows it'} right={<span className="text-xs text-slate-500">files are encrypted at rest; up to 25 MB each</span>}>
        {item.evidence.length > 0 ? (
          <ul className="space-y-1.5 text-sm mb-3">
            {item.evidence.map((e) => (
              <li key={e.id} className="flex items-center gap-2 flex-wrap border border-slate-200 rounded-xl px-3 py-2">
                <a className="font-semibold text-orange-800 underline" href={downloadUrl(`/items/${id}/evidence/${e.id}`)}>{e.file_name}</a>
                <span className="text-xs text-slate-500">{Math.round(e.size_bytes / 1024)} KB · {e.uploaded_by_name || e.uploaded_by_email || (e.via === 'email' ? 'by email' : '')} · {fmtWhen(e.created_at)}</span>
                {e.note && <span className="text-xs text-slate-600 basis-full">{e.note}</span>}
                {compliance && <button type="button" className="ml-auto text-xs text-slate-400 hover:text-rose-700" onClick={() => { if (window.confirm(`Remove "${e.file_name}" from this item?`)) api(`/items/${id}/evidence/${e.id}`, { method: 'DELETE' }).then((d) => { if (d?.success) { setItem(d.item); onChanged?.(); } else setErr(d?.message || 'Could not remove'); }); }}>Remove</button>}
              </li>
            ))}
          </ul>
        ) : <div className="text-sm text-slate-500 mb-3">No files yet.</div>}
        {!closed && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Field label="Attach files" hint="PDF, Word, Excel, CSV, text, images, or an email (.eml, .msg)">
                <input type="file" multiple accept={ACCEPT} className="block w-full text-sm text-slate-700 file:mr-3 file:rounded-xl file:border file:border-slate-200 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-slate-700 hover:file:bg-slate-50" onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5))} />
              </Field>
              <input className={inputCls} value={fileNote} onChange={(e) => setFileNote(e.target.value)} placeholder="One line on what this shows (optional)" />
              <Button disabled={busy || !files.length} onClick={uploadFiles}>{files.length > 1 ? `Attach ${files.length} files` : 'Attach'}</Button>
              {!compliance && <div className="text-xs text-slate-500">Attaching sends the item to compliance for review.</div>}
            </div>
            {!compliance && (
              <div className="space-y-2">
                <Field label={item.kind === 'read' ? 'Read and acknowledged' : 'It is in place: say how, in one line'}>
                  <textarea rows={3} className={inputCls} value={how} onChange={(e) => setHow(e.target.value)} placeholder={item.kind === 'read' ? 'Read and understood; who was told' : 'Where the process lives, who runs it, since when'} />
                </Field>
                <div className="flex gap-2 flex-wrap">
                  <Button kind="good" disabled={busy || !how.trim()} onClick={async () => { if (await post(`/items/${id}/confirm`, { how })) setHow(''); }}>{item.kind === 'read' ? 'Acknowledge' : 'Confirm it is in place'}</Button>
                  <Button kind="secondary" disabled={busy} onClick={() => post(`/items/${id}/submit`, { note: how })}>Send to compliance as is</Button>
                </div>
              </div>
            )}
          </div>
        )}
        {ownerLines.length > 0 && (
          <div className="mt-4">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Answers</div>
            <ul className="space-y-1 text-sm">{ownerLines.map((l) => <li key={l.id} className="text-slate-700"><b>{l.user_name || l.person_name || ''}</b>{l.answer ? ` · ${l.answer}` : ''}{l.answer_note ? `: ${l.answer_note}` : ''} <span className="text-xs text-slate-500">{l.answered_at ? fmtWhen(l.answered_at) : l.status}</span></li>)}</ul>
          </div>
        )}
        {err && <div className="text-xs text-rose-600 mt-2">{err}</div>}
      </Card>

      {/* Compliance controls */}
      {compliance && assign && (
        <Card title="Departments, accountable people, answer type and due date" right={<span className="text-xs text-slate-500">one or more departments; each answers its own part</span>}>
          {(() => {
            const users = people?.users || [];
            const chosen = assign.department_ids;
            const leadOf = (dId) => { const u = users.filter((x) => x.department_id === dId); return (u.find((x) => x.user_type === 'department_lead') || u[0] || {}).id || null; };
            const toggleDept = (dId) => {
              if (chosen.includes(dId)) setAssign({ ...assign, department_ids: chosen.filter((x) => x !== dId), accountable_user_ids: assign.accountable_user_ids.filter((id) => users.find((u) => u.id === id)?.department_id !== dId) });
              else { const lead = leadOf(dId); setAssign({ ...assign, department_ids: [...chosen, dId], accountable_user_ids: lead && !assign.accountable_user_ids.includes(lead) ? [...assign.accountable_user_ids, lead] : assign.accountable_user_ids }); }
            };
            const toggleAcc = (uId) => setAssign({ ...assign, accountable_user_ids: assign.accountable_user_ids.includes(uId) ? assign.accountable_user_ids.filter((x) => x !== uId) : [...assign.accountable_user_ids, uId] });
            return (
              <div className="space-y-3">
                <Field label="Departments on this item" hint="click to add or remove; the first one owns it">
                  <div className="flex flex-wrap gap-1 py-1">
                    {(people?.departments || []).map((d) => { const on = chosen.includes(d.id); return (
                      <button key={d.id} type="button" onClick={() => toggleDept(d.id)} className={`text-[11px] px-2 py-0.5 rounded-full ring-1 ${on ? 'bg-orange-50 text-orange-800 ring-orange-200 font-semibold' : 'bg-white text-slate-500 ring-slate-200 hover:ring-orange-200'}`}>{on ? '✓ ' : ''}{d.name}</button>
                    ); })}
                  </div>
                </Field>
                {chosen.length === 0 ? <div className="text-xs text-rose-700">No department. Pick at least one.</div> : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {chosen.map((dId) => { const d = (people?.departments || []).find((x) => x.id === dId); const members = users.filter((u) => u.department_id === dId); return (
                      <div key={dId} className="border border-slate-200 rounded-xl px-3 py-2">
                        <div className="text-xs font-semibold text-slate-700">{d?.name || 'Department'} <span className="font-normal text-slate-500">· accountable, one or more</span></div>
                        <div className="flex flex-wrap gap-1 py-1">
                          {members.map((u) => { const on = assign.accountable_user_ids.includes(u.id); return (
                            <button key={u.id} type="button" onClick={() => toggleAcc(u.id)} className={`text-[11px] px-2 py-0.5 rounded-full ring-1 ${on ? 'bg-orange-50 text-orange-800 ring-orange-200 font-semibold' : 'bg-white text-slate-500 ring-slate-200 hover:ring-orange-200'}`}>{on ? '✓ ' : ''}{u.name}</button>
                          ); })}
                          {!members.length && <span className="text-xs text-rose-700">No one in this department</span>}
                        </div>
                        <div className="text-[11px] text-slate-500">Everyone in {d?.name || 'it'} is on the item.</div>
                      </div>
                    ); })}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label="Answer type">
                    <select className={inputCls} value={assign.kind} onChange={(e) => setAssign({ ...assign, kind: e.target.value })}>
                      {Object.entries(KIND_WORDS).map(([k, v]) => <option key={k} value={k}>{v} · {KIND_HINT[k]}</option>)}
                    </select>
                  </Field>
                  <Field label="Due"><input type="date" className={inputCls} value={assign.due_date} onChange={(e) => setAssign({ ...assign, due_date: e.target.value })} /></Field>
                </div>
              </div>
            );
          })()}
          <div className="mt-3 flex gap-2 flex-wrap">
            <Button disabled={busy} onClick={() => patch({ accountable_user_ids: assign.accountable_user_ids, department_ids: assign.department_ids, kind: assign.kind, due_date: assign.due_date || null })}>Save</Button>
            {item.owner_set_by === 'human' && <span className="text-xs text-slate-500 self-center">Set by hand; a roster re-apply leaves it alone.</span>}
          </div>
        </Card>
      )}

      {compliance && (
        <Card title="Decide">
          <div className="flex flex-wrap gap-2 items-end">
            <Field label="Reason or note (kept in the record)" className="flex-1 min-w-[260px]"><input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why, in a few words" /></Field>
            {!closed && <Button kind="good" disabled={busy} onClick={() => markReady(false)}>Mark ready</Button>}
            {!closed && item.status !== 'at_risk' && <Button kind="secondary" disabled={busy} onClick={() => patch({ status: 'at_risk', reason })}>Flag at risk</Button>}
            {item.applicable && <Button kind="secondary" disabled={busy || !reason.trim()} title={reason.trim() ? '' : 'Give the reason first'} onClick={() => patch({ applicable: false, na_reason: reason })}>Does not apply</Button>}
            {closed && <Button kind="secondary" disabled={busy} onClick={() => patch({ status: 'reopen', reason })}>Reopen</Button>}
            {!item.applicable && <Button kind="secondary" disabled={busy} onClick={() => patch({ applicable: true, reason })}>Applies after all</Button>}
          </div>
          {confirmReady && !closed && (
            <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3" role="alertdialog" aria-live="polite">
              <div className="text-sm font-semibold text-amber-900">Nothing has been attached and no policy has been selected for this item.</div>
              <div className="text-sm text-amber-900 mt-1">Marking it ready records your confirmation alone as the basis. Confirm, or attach what shows it first.</div>
              <div className="mt-2 flex gap-2 flex-wrap">
                <Button kind="good" disabled={busy} onClick={() => markReady(true)}>Yes, mark it ready on my confirmation</Button>
                <Button kind="secondary" onClick={() => setConfirmReady(false)}>Not yet</Button>
              </div>
            </div>
          )}
          {err && <div className="text-xs text-rose-600 mt-2">{err}</div>}
          <div className="text-xs text-slate-500 mt-2">Every decision is written to the record with your name and the time.</div>
        </Card>
      )}

      {/* History */}
      <Card title="History">
        {item.events.length === 0 ? <Empty>Nothing yet.</Empty> : (
          <ul className="space-y-1.5 text-sm">
            {item.events.map((e) => (
              <li key={e.id} className="grid grid-cols-[150px_1fr] gap-3"><span className="text-xs text-slate-500">{fmtWhen(e.created_at)}</span><span className="text-slate-700">{words(e)}</span></li>
            ))}
          </ul>
        )}
      </Card>
      <div className="text-right"><Button kind="ghost" onClick={onClose}>Back</Button></div>
    </div>
  );
}

function Box({ label, children }) {
  return <div className="bg-slate-50 rounded-xl px-3 py-2"><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div><div className="text-slate-800 mt-0.5 leading-relaxed">{children || <span className="text-slate-400">Not written</span>}</div></div>;
}

function words(e) {
  const who = e.actor_name || e.actor_email || (e.via === 'agent' ? 'The agent' : 'Someone');
  const d = e.detail || {};
  if (e.action === 'evidence_added') return `${who} attached "${d.file_name}"`;
  if (e.action === 'evidence_removed') return `${who} removed "${d.file_name}"`;
  if (e.action === 'evidence_downloaded') return `${who} downloaded "${d.file_name}"`;
  if (e.action === 'item_confirmed') return `${who} confirmed it is in place: ${d.how || ''}`;
  if (e.action === 'item_submitted') return `${who} sent the item to compliance${d.note ? `: ${d.note}` : ''}`;
  if (e.action === 'policy_checked') return `${who} checked the policy library: ${d.found || 0} match${d.found === 1 ? '' : 'es'} among ${(d.candidates || []).length} candidate${(d.candidates || []).length === 1 ? '' : 's'}`;
  if (e.action === 'policy_accepted') return `${who} accepted ${d.policy} and closed the item by policy`;
  if (e.action === 'policy_rejected') return `${who} set aside ${d.policy}`;
  if (e.action === 'item_updated') {
    const parts = [];
    if (d.status) parts.push(`status ${d.status.from} → ${d.status.to}${d.status.reason ? ` (${d.status.reason})` : ''}${d.status.nothing_attached_acknowledged ? ' (confirmed with nothing attached and no policy selected)' : ''}`);
    if (d.owner) parts.push(Array.isArray(d.owner.accountable) && d.owner.accountable.length > 1 ? `accountable: ${d.owner.accountable.join(', ')}` : `accountable ${d.owner.from || 'no one'} → ${d.owner.to || 'no one'}`);
    if (d.applicable) parts.push(d.applicable.to ? 'marked as applying' : `marked not applicable${d.applicable.reason ? ` (${d.applicable.reason})` : ''}`);
    if (d.kind) parts.push(`answer type ${d.kind.from} → ${d.kind.to}`);
    if (d.due_date) parts.push(`due ${d.due_date.from || 'none'} → ${d.due_date.to || 'none'}`);
    if (d.wording) parts.push('approved the plain wording');
    if (d.department) parts.push('changed the department');
    if (d.people) parts.push(`${d.people.to} people on it`);
    return `${who}: ${parts.join('; ') || 'updated the item'}`;
  }
  return `${who}: ${String(e.action).replace(/_/g, ' ')}`;
}
